import { PartialNullable } from "../../Extensions";
import { IJNativeEvent } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyString } from "../../RadObject";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { NAMESPACE } from "./_Namespace";

import "./CheckBox.scss";

export enum CheckBoxTypeEnum
{
    checkBox = "checkBox",
    radio = "radio",
}

export type CheckBoxPropertyValues =
    InputControlPropertyValues
    & {
        labelText: string;
        textYesNo: boolean;
        type: CheckBoxTypeEnum;
    };

export type CheckBoxProperties = {
    [name in keyof CheckBoxPropertyValues]: Property<CheckBoxPropertyValues[name]>
};

export interface ICheckBox extends IInputControl, CheckBoxProperties
{
}

export class CheckBox extends InputControl implements ICheckBox
{
    // #region Static

    public static override classNamespace = NAMESPACE + "CheckBox";
    public static override className = "CheckBox";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<CheckBoxPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                type: CheckBoxTypeEnum.checkBox,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    protected _labelElementRef!: ElementRef;
    protected _valueElementRef!: ElementRef<HTMLInputElement>;

    // #endregion

    // #region Properties

    public labelText!: PropertyString;
    public textYesNo!: PropertyBoolean;
    public type!: Property<CheckBoxTypeEnum>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<CheckBoxPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-check-box ui checkbox");

        // Trigger Property Changes
        this.labelText.triggerChange();
        this.textYesNo.triggerChange();
        this.type.triggerChange();
    }

    // #endregion

    // #region Public Methods

    // TODO: These do not conform to standard

    public textGet()
    {
        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get(),
            textYesNo = this.textYesNo.get();

        if (!dataSource || !dataSourceProperty)
            return null;

        const
            value = dataSource.propertyValueByPathGet(dataSourceProperty);

        if (textYesNo)
            return value ? "Yes" : "No";

        return value;
    }

    // #endregion

    // #region Protected Methods

    protected _elementValueChanged()
    {
        if (!this.__processElementValueChanged)
            return;

        this.value.set(this._elementValueGet());
    }
    protected _elementValueGet()
    {
        return this._valueElementRef.current?.is(":checked") ?? false;
    }
    protected _elementValueSet(value?: any)
    {
        this.__processElementValueChanged = false;
        this._valueElementRef.current.prop("checked", value === true);
        this.__processElementValueChanged = true;
    }
    protected _labelTextChanged(change: PropertyChange<string>)
    {
        //this._labelElementRef.current.text(change.currentValue);
    }
    protected _textYesNoChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _typeChanged(change: PropertyChange<string>)
    {
        if (change.currentValue === CheckBoxTypeEnum.radio)
            this.addCssClass("radio");
        else
            this.removeCssClass("radio");

        this._valueElementRef.current.attr("type", change.currentValue?.toLowerCase() ?? null);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: CheckBoxPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._labelElementRef = new ElementRef();
        this._valueElementRef = new ElementRef();

        this.labelText = new PropertyString(this, propertyValues.labelText, this._labelTextChanged);
        this.textYesNo = new PropertyBoolean(this, propertyValues.textYesNo, this._textYesNoChanged);
        this.type = new Property<CheckBoxTypeEnum>(this, "System.String, System.Private.CoreLib", propertyValues.type, this._typeChanged);

        this.value.overrideGet(next =>
        {
            return !!next();
        });
        this.value.overrideSet((value, next) =>
        {
            next(!!value);

            if (value !== true && value !== false)
                this._updateDataSource(); // Check box must be either true or false; update data source
        });
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        this._valueElementRef.current.on("change", { self: this }, this.onValueElementChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.labelTargetId = this._valueElementRef.current.attr("id") ?? undefined;

        return $$element;
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <input ref={this._valueElementRef} id={`${this.controlId}-input`} type={this.type.get()?.toLowerCase()}></input>
                <label ref={this._labelElementRef} for={`${this.controlId}-input`}>{children}</label>
            </div>
        );
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        if (change.currentValue)
            this._valueElementRef.current.removeAttr("disabled");
        else
            this._valueElementRef.current.attr("disabled", "disabled");
    }
    protected override _valueChanged(change: PropertyChange<any>)
    {
        super._valueChanged(change);
        this._elementValueSet(change.currentValue);
    }

    // #endregion

    // #region Event Handlers

    protected onValueElementChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._elementValueChanged();
    }

    // #endregion

    // #endregion
}

